import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { NetworthChart } from "../components/dashboard/NetworthChart";
import { CardListStichtage } from "../components/mobile/CardListStichtage";
import { InclusionFilter } from "../components/networth/InclusionFilter";
import { NetworthChartCard } from "../components/networth/NetworthChartCard";
import { NetworthHistoryTable } from "../components/networth/NetworthHistoryTable";
import { SnapshotDrillModal } from "../components/networth/SnapshotDrillModal";
import { SnapshotFormModal } from "../components/networth/SnapshotFormModal";
import { type ClassKey, SparklinePanel } from "../components/networth/SparklinePanel";
import {
  type NetworthPeriod,
  useBackfillHistorical,
  useBackfillStatus,
  useCleanupZeroSnapshots,
  useNetworthHistory,
  useNetworthSnapshots,
  useNetworthSummary,
} from "../hooks/networth";
import { useIsMobile } from "../hooks/useIsMobile";
import { formatDateShort } from "../lib/format";
import { adjustClasses } from "../lib/networth-utils";

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function Networth() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [modalOpen, setModalOpen] = useState(false);
  const summary = useNetworthSummary();
  const list = useNetworthSnapshots({ limit: 50 });
  const backfill = useBackfillHistorical();
  const cleanup = useCleanupZeroSnapshots();
  // Auf true: Status wird einmalig beim Mount geholt, polling startet
  // selbsttaetig wenn Job laeuft (refetchInterval-Funktion im Hook).
  const [pollEnabled, setPollEnabled] = useState(true);
  const backfillStatusQuery = useBackfillStatus(pollEnabled);
  const job = backfillStatusQuery.data;
  const jobRunning = job?.status === "running";
  const [backfillStatus, setBackfillStatus] = useState<string | null>(null);

  // Chart-Toolbar State (Tasks 12+13)
  const [period, setPeriod] = useState<NetworthPeriod>("3J");
  const historyQuery = useNetworthHistory(period);
  const fullHistory = useMemo(() => historyQuery.data?.history ?? [], [historyQuery.data]);
  const excludablePositions = useMemo(
    () => historyQuery.data?.excludablePositions ?? [],
    [historyQuery.data],
  );

  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("networth.excludedPositionIds");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    localStorage.setItem("networth.excludedPositionIds", JSON.stringify(Array.from(excludedIds)));
  }, [excludedIds]);

  const [soloClass, setSoloClass] = useState<ClassKey | null>(null);
  const [showYoy, setShowYoy] = useState(false);
  const [drillStichtag, setDrillStichtag] = useState<string | null>(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const adjusted = useMemo(
    () => adjustClasses(fullHistory, excludablePositions, excludedIds),
    [fullHistory, excludablePositions, excludedIds],
  );

  function toggleExcluded(id: string) {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSolo(key: ClassKey) {
    setSoloClass((cur) => (cur === key ? null : key));
  }

  async function handleCleanup() {
    if (
      !window.confirm(
        t("networth.list.cleanup_confirm", {
          defaultValue:
            "Alle Snapshots mit Nettovermögen=0 € löschen? (Typischerweise leere Auto-Snapshots aus der Initial-Phase.)",
        }),
      )
    )
      return;
    try {
      const result = await cleanup.mutateAsync();
      setBackfillStatus(
        t("networth.list.cleanup_success", {
          defaultValue: "{{count}} Null-Stichtage entfernt.",
          count: result.deletedDays,
        }),
      );
    } catch (err) {
      setBackfillStatus(
        `${t("networth.list.cleanup_failed", { defaultValue: "Fehler" })}: ${(err as Error).message}`,
      );
    }
  }

  async function handleBackfill(force: boolean) {
    setBackfillStatus(null);
    try {
      const ack = await backfill.mutateAsync({ intervalDays: 7, force });
      if (ack.status === "already_running") {
        setBackfillStatus(
          t("networth.list.backfill_already_running", {
            defaultValue: "Ein Backfill läuft bereits — Status unten.",
          }),
        );
      }
      // Polling anstossen — laeuft bis status != "running".
      setPollEnabled(true);
    } catch (err) {
      setBackfillStatus(
        `${t("networth.list.backfill_failed", { defaultValue: "Fehler" })}: ${
          (err as Error).message
        }`,
      );
    }
  }

  const snapshots = list.data?.snapshots ?? [];
  const isEmpty = snapshots.length === 0;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_assets")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("networth.list.title")}</h1>
          {list.data && (
            <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              {list.data.total} {t("networth.snapshots_plural", { defaultValue: "Snapshots" })}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/otterfolio"
            className="px-3 py-2 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5"
            style={{
              borderColor: "#2E7D4F",
              background: "rgba(76, 175, 110, 0.08)",
              color: "#2E7D4F",
            }}
            title={t("networth.list.otterfolio_hint")}
          >
            <span aria-hidden>🦦</span>
            {t("networth.list.otterfolio_link")}
          </Link>
          <button
            type="button"
            onClick={handleCleanup}
            disabled={cleanup.isPending}
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-50"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            title={t("networth.list.cleanup_hint", {
              defaultValue:
                "Snapshots mit Nettovermögen=0€ löschen — alte Auto-Snapshots aus der Initial-Phase wegräumen.",
            })}
          >
            {cleanup.isPending
              ? `${t("networth.list.cleanup_running", { defaultValue: "Räume auf" })}…`
              : t("networth.list.cleanup_btn", { defaultValue: "0€-Tage löschen" })}
          </button>
          <button
            type="button"
            onClick={() => handleBackfill(true)}
            disabled={backfill.isPending || jobRunning}
            className="px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-50"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            title={t("networth.list.backfill_hint", {
              defaultValue:
                "Wöchentliche Snapshots ab erstem Investment-Kauf bis heute (force = überschreibt 0€-Tage). Läuft im Hintergrund.",
            })}
          >
            {jobRunning
              ? `${t("networth.list.backfill_running", { defaultValue: "Historie wird gefüllt" })}…`
              : t("networth.list.backfill_btn", { defaultValue: "Historie nachfüllen" })}
          </button>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            + {t("networth.list.new")}
          </button>
        </div>
      </div>

      {backfillStatus && (
        <div
          className="fv-card p-3 text-sm flex items-start justify-between gap-3"
          style={{
            background: backfill.isError ? "var(--neg-soft)" : "var(--pos-soft)",
            color: backfill.isError ? "var(--neg)" : "var(--pos)",
          }}
        >
          <span>{backfillStatus}</span>
          <button
            type="button"
            className="text-xs"
            onClick={() => setBackfillStatus(null)}
            style={{ color: "var(--muted)" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Status-Karte nur einblenden wenn was los ist (kein dauerhaftes Banner). */}
      {job &&
        (job.status === "running" || job.status === "completed" || job.status === "failed") && (
          <div
            className="fv-card p-4 space-y-2"
            style={{
              background:
                job.status === "running"
                  ? "var(--accent-soft)"
                  : job.status === "failed"
                    ? "var(--neg-soft)"
                    : "var(--pos-soft)",
            }}
          >
            <div className="flex justify-between items-center text-sm">
              <span className="font-semibold">
                {job.status === "running"
                  ? "Historie wird gefüllt im Hintergrund"
                  : job.status === "completed"
                    ? "Historie nachgefüllt"
                    : "Backfill fehlgeschlagen"}
              </span>
              {job.status === "running" && (
                <span className="num font-semibold">
                  {job.processedDays}
                  {job.totalDays > 0 ? ` / ${job.totalDays}` : ""} Tage
                </span>
              )}
              {job.status !== "running" && (
                <button
                  type="button"
                  className="text-xs"
                  onClick={() => setPollEnabled(false)}
                  style={{ color: "var(--muted)" }}
                >
                  ✕
                </button>
              )}
            </div>
            {job.status === "running" && (
              <>
                <div
                  className="h-2 rounded-full overflow-hidden"
                  style={{ background: "var(--border-soft)" }}
                >
                  <div
                    className="h-full transition-all"
                    style={{
                      background: "var(--accent)",
                      width: `${job.totalDays > 0 ? Math.min(100, (job.processedDays / job.totalDays) * 100) : 0}%`,
                    }}
                  />
                </div>
                <p className="text-xs" style={{ color: "var(--muted)" }}>
                  Du kannst die Seite ruhig wechseln — der Job läuft serverseitig weiter.
                </p>
              </>
            )}
            {job.status === "completed" && (
              <p className="text-xs">
                {job.processedDays} Snapshots verarbeitet ({job.daysFrom} – {job.daysUntil}).
              </p>
            )}
            {job.status === "failed" && <p className="text-xs">Fehler: {job.error}</p>}
          </div>
        )}

      {summary.data?.isStale && summary.data.daysSinceLastSnapshot !== null && (
        <div
          className="fv-card p-3 text-sm border-l-4"
          style={{
            borderLeftColor: "var(--warn)",
            background: "var(--warn-soft)",
            color: "var(--text)",
          }}
        >
          {t("networth.list.stale_banner", { days: summary.data.daysSinceLastSnapshot })}
        </div>
      )}

      {isEmpty ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-3">
            {t("networth.list.empty")}
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("networth.list.empty_cta")}
          </button>
        </div>
      ) : (
        <>
          {summary.data?.latest && (
            <div className="fv-card p-6">
              <div className="fv-kpi-label mb-1">{t("networth.list.summary.total")}</div>
              <div className="text-4xl font-bold tracking-tight num">
                {formatEur(summary.data.latest.totalCents)}
              </div>
              <div className="text-xs mt-2 num" style={{ color: "var(--muted)" }}>
                {t("networth.list.summary.as_of", {
                  date: formatDateShort(summary.data.latest.stichtag),
                })}
              </div>
            </div>
          )}

          {isMobile ? (
            <div className="space-y-4">
              <NetworthChartCard onDatapointClick={setDrillStichtag} />
              {excludablePositions.length > 0 && (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setFilterSheetOpen(true)}
                    className="px-3 py-2 text-sm rounded-md border min-h-[44px] flex items-center gap-2"
                    style={{ borderColor: "var(--border)", color: "var(--text)" }}
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      aria-hidden="true"
                    >
                      <path d="M3 6h18M6 12h12M10 18h4" />
                    </svg>
                    {t("networth.filter.toolbar_btn", { defaultValue: "Filter" })}
                  </button>
                </div>
              )}
              <SparklinePanel data={adjusted} activeClass={soloClass} onClassClick={toggleSolo} />
            </div>
          ) : (
            <div className="fv-card p-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-1 flex-wrap">
                  {(["1M", "3M", "6M", "YTD", "1J", "3J", "5J", "all"] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPeriod(p)}
                      className="px-2.5 py-1 text-xs rounded-md font-medium transition-colors"
                      style={
                        period === p
                          ? { background: "var(--text)", color: "var(--bg)" }
                          : { color: "var(--muted)", background: "var(--card-elev)" }
                      }
                    >
                      {p === "all" ? "Max" : p}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showYoy}
                    onChange={(e) => setShowYoy(e.target.checked)}
                  />
                  <span style={{ color: "var(--muted)" }}>Vorjahresvergleich</span>
                </label>
              </div>

              <InclusionFilter
                positions={excludablePositions}
                excludedIds={excludedIds}
                onToggle={toggleExcluded}
              />

              <NetworthChart
                embedded
                height={380}
                externalData={adjusted}
                soloClass={soloClass}
                showYoy={showYoy}
                onDatapointClick={setDrillStichtag}
              />

              <SparklinePanel data={adjusted} activeClass={soloClass} onClassClick={toggleSolo} />
            </div>
          )}

          {drillStichtag && (
            <SnapshotDrillModal stichtag={drillStichtag} onClose={() => setDrillStichtag(null)} />
          )}

          {isMobile && filterSheetOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                style={{ background: "rgba(0,0,0,0.5)" }}
                onClick={() => setFilterSheetOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setFilterSheetOpen(false);
                }}
                role="presentation"
                aria-hidden="true"
              />
              <div
                className="fixed left-0 right-0 bottom-0 z-50 rounded-t-2xl p-4"
                style={{
                  background: "var(--card)",
                  borderTop: "1px solid var(--border)",
                  maxHeight: "70vh",
                  overflowY: "auto",
                  paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
                }}
              >
                <div className="flex justify-center pb-2">
                  <div
                    className="w-12 h-1 rounded-full"
                    style={{ background: "var(--border)" }}
                    aria-hidden="true"
                  />
                </div>
                <InclusionFilter
                  positions={excludablePositions}
                  excludedIds={excludedIds}
                  onToggle={toggleExcluded}
                />
                <button
                  type="button"
                  onClick={() => setFilterSheetOpen(false)}
                  className="w-full mt-4 px-3 py-2 text-sm font-medium rounded-md min-h-[44px]"
                  style={{ background: "var(--accent)", color: "white" }}
                >
                  {t("common.apply", { defaultValue: "Übernehmen" })}
                </button>
              </div>
            </>
          )}

          {list.data && list.data.total > 50 && (
            <p className="text-xs" style={{ color: "var(--muted)" }}>
              {t("networth.list.limit_hint", { count: 50, total: list.data.total })}
            </p>
          )}

          {isMobile ? (
            <CardListStichtage snapshots={snapshots} onOpenDrill={setDrillStichtag} />
          ) : (
            <NetworthHistoryTable snapshots={snapshots} />
          )}
        </>
      )}

      <SnapshotFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
